import { Paragraph } from "../types";

export interface TimedWord {
  word: string;
  start: number;
  end: number;
}

export interface Caption {
  text: string;
  start: number;
  end: number;
  words: TimedWord[];
}

// Keep in sync with group_words_into_captions in
// docker-containers-for-lambda-functions/export-video/subtitles.py so the
// preview breaks lines exactly where the exported video does.
const MAX_CHARS = 42;
const MAX_TIME = 2.5;
const SOFT_MIN_CHARS = 12;
const CLAUSE_BREAK_RATIO = 0.6;
const MIN_LINE_DURATION = 0.7;
const MIN_LINE_CHARS = 8;
const MERGE_SLACK = 1.25;
const MAX_GAP = 0.4;
const CAPITAL_GAP = 0.15;

const SENTENCE_END_RE = /[.!?]["')\]]?$/;
const CLAUSE_END_RE = /[,;:–—-]$/;

const lineChars = (line: TimedWord[]) =>
  line.reduce((sum, w) => sum + w.word.length, 0) +
  Math.max(0, line.length - 1);

const startsNewSentence = (word: TimedWord, prevEnd: number) => {
  const text = word.word;
  const isCapitalised =
    text.charAt(0) !== text.charAt(0).toLowerCase() &&
    (text.length > 1 || text === "I");
  return isCapitalised && word.start - prevEnd >= CAPITAL_GAP;
};

export function groupWordsIntoCaptions(
  paragraphs: Paragraph[],
  maxWords = 0,
): Caption[] {
  const words: TimedWord[] = paragraphs
    .flatMap((p) => p.words ?? [])
    .filter(
      (w): w is TimedWord =>
        typeof w.start === "number" &&
        typeof w.end === "number" &&
        !!String(w.word ?? "").trim(),
    );
  if (!words.length) return [];

  const wordLimit = maxWords > 0 ? maxWords : Infinity;

  const lines: TimedWord[][] = [];
  let line: TimedWord[] = [];
  words.forEach((word, i) => {
    if (line.length) {
      const prevEnd = line[line.length - 1].end;
      const forced =
        lineChars(line) + 1 + word.word.length > MAX_CHARS ||
        word.end - line[0].start > MAX_TIME ||
        word.start - prevEnd > MAX_GAP ||
        startsNewSentence(word, prevEnd) ||
        line.length >= wordLimit;
      if (forced) {
        lines.push(line);
        line = [];
      }
    }
    line.push(word);

    const chars = lineChars(line);
    const naturalBreak =
      (SENTENCE_END_RE.test(word.word) && chars >= SOFT_MIN_CHARS) ||
      (CLAUSE_END_RE.test(word.word) &&
        chars >= MAX_CHARS * CLAUSE_BREAK_RATIO);
    if (i === words.length - 1 || naturalBreak) {
      lines.push(line);
      line = [];
    }
  });

  let i = 0;
  while (i < lines.length && lines.length > 1) {
    const current = lines[i];
    const tooShort =
      current.length === 1 ||
      lineChars(current) < MIN_LINE_CHARS ||
      current[current.length - 1].end - current[0].start < MIN_LINE_DURATION;
    if (!tooShort) {
      i += 1;
    } else {
      const prev = i > 0 ? lines[i - 1] : null;
      const next = i + 1 < lines.length ? lines[i + 1] : null;
      const limit = MAX_CHARS * MERGE_SLACK;
      const gapBefore = prev
        ? current[0].start - prev[prev.length - 1].end
        : Infinity;
      const gapAfter = next
        ? next[0].start - current[current.length - 1].end
        : Infinity;
      const currentStartsSentence =
        prev && startsNewSentence(current[0], prev[prev.length - 1].end);
      const nextStartsSentence =
        next && startsNewSentence(next[0], current[current.length - 1].end);

      if (
        prev &&
        !currentStartsSentence &&
        gapBefore <= MAX_GAP &&
        lineChars([...prev, ...current]) <= limit &&
        prev.length + current.length <= wordLimit
      ) {
        lines[i - 1] = [...prev, ...current];
        lines.splice(i, 1);
        i -= 1;
      } else if (
        next &&
        !nextStartsSentence &&
        gapAfter <= MAX_GAP &&
        lineChars([...current, ...next]) <= limit &&
        current.length + next.length <= wordLimit
      ) {
        lines[i + 1] = [...current, ...next];
        lines.splice(i, 1);
      } else {
        i += 1;
      }
    }
  }

  return lines.map((l) => ({
    text: l.map((w) => w.word).join(" "),
    start: l[0].start,
    end: l[l.length - 1].end,
    words: l,
  }));
}

export function findCaptionAt(captions: Caption[], timeSec: number) {
  return captions.find((c) => timeSec >= c.start && timeSec <= c.end) ?? null;
}

// Index of the word being spoken; between words the previous one stays active
// so the highlight doesn't flicker off during short pauses.
export function activeWordIndex(caption: Caption, timeSec: number): number {
  let index = -1;
  caption.words.forEach((w, i) => {
    if (w.start <= timeSec) index = i;
  });
  return index;
}
